/**
 * LexisNexisCallLogPage — Compliance & Capture · LexisNexis Call Log.
 *
 * Read-only audit of every LexisNexis call (KBA / InstantID-search / DDP
 * fraud verification). The underlying dbo.LexisNexisCallLogs stores SHA-256
 * hashes (RequestHash + RequestPiiHash), NOT raw payloads — the detail modal
 * shows metadata + hashes only. Even when LexisNexis goes live, the
 * sanitization pass writes pre-redacted JSON before any row is hashed.
 *
 * Read: client.retail.admin.lexisNexisConfig.listCalls(filter)
 *       → GET /v3/admin/lexisnexis/call-log (Admin+)
 *
 * Migrated 2026-06-16 from ROP-teams Admin (operational app surfaces no
 * config / audit editors). Read-only — no writes.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApiError } from '@retail-ops/api-client';
import type { LexisNexisCallLogRow, LexisNexisCallLogFilter } from '@retail-ops/shared-types';
import { useApiClient } from '@/contexts/ApiClientContext';
import { Banner, Button, Card, Pill, Spinner } from '@/components/primitives';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { TextField, SelectField, NumberField } from '@/components/FormField';
import { ModalShell } from '@/components/ModalShell';

const DEFAULT_TOP = 100;

const PRODUCT_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '', label: 'All operations' },
  { value: 'kba.generate', label: 'KBA — generate' },
  { value: 'kba.verify', label: 'KBA — verify' },
  { value: 'instantid.search', label: 'InstantID — search' },
  { value: 'InstantID', label: 'InstantID (DDP)' },
  { value: 'InstantIdPlusFraudPoint', label: 'InstantID + FraudPoint' },
  { value: 'FraudPoint', label: 'FraudPoint' },
  { value: 'Emailage', label: 'Emailage' },
  { value: 'FIS-IdentityMisuse', label: 'FIS Identity Misuse' },
];

function fmtTs(iso: string | undefined | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function fmtLatency(ms: number): string {
  if (!ms || ms < 0) return '—';
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(2)} s`;
}

function fmtShortHash(hex: string | null | undefined): string {
  if (!hex) return '—';
  return hex.length > 12 ? `${hex.slice(0, 8)}…${hex.slice(-4)}` : hex;
}

export function LexisNexisCallLogPage(): JSX.Element {
  const client = useApiClient();
  const [rows, setRows] = useState<ReadonlyArray<LexisNexisCallLogRow> | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selected, setSelected] = useState<LexisNexisCallLogRow | null>(null);

  // Staged filter inputs.
  const [fromInput, setFromInput] = useState('');
  const [toInput, setToInput] = useState('');
  const [cartIdInput, setCartIdInput] = useState('');
  const [productInput, setProductInput] = useState('');
  const [topInput, setTopInput] = useState<number | null>(DEFAULT_TOP);
  const [applied, setApplied] = useState<LexisNexisCallLogFilter>({ top: DEFAULT_TOP });
  const [refreshNonce, setRefreshNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setRows(null);
      setLoadError(null);
      try {
        const res = await client.retail.admin.lexisNexisConfig.listCalls(applied);
        if (cancelled) return;
        setRows(res?.rows ? [...res.rows] : []);
      } catch (e) {
        if (cancelled) return;
        setLoadError(
          ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e),
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, applied, refreshNonce]);

  const refresh = useCallback(() => setRefreshNonce((n) => n + 1), []);

  const applyFilters = useCallback(() => {
    setApplied({
      top: topInput ?? DEFAULT_TOP,
      ...(fromInput ? { fromUtc: new Date(fromInput).toISOString() } : {}),
      ...(toInput ? { toUtc: new Date(toInput).toISOString() } : {}),
      ...(cartIdInput.trim() ? { cartId: cartIdInput.trim() } : {}),
      ...(productInput ? { product: productInput } : {}),
    });
  }, [fromInput, toInput, cartIdInput, productInput, topInput]);

  const clearFilters = useCallback(() => {
    setFromInput('');
    setToInput('');
    setCartIdInput('');
    setProductInput('');
    setTopInput(DEFAULT_TOP);
    setApplied({ top: DEFAULT_TOP });
  }, []);

  const productCounts = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of rows ?? []) m.set(r.product, (m.get(r.product) ?? 0) + 1);
    return [...m.entries()];
  }, [rows]);

  const columns: ReadonlyArray<DataTableColumn<LexisNexisCallLogRow>> = useMemo(
    () => [
      { header: 'Timestamp', cell: (r) => <span className="cell-desc">{fmtTs(r.timestamp)}</span> },
      { header: 'Operation', cell: (r) => <code className="cell-mono">{r.product}</code> },
      {
        header: 'Cart',
        cell: (r) =>
          r.cartId ? <code className="cell-mono" title={r.cartId}>{r.cartId.slice(0, 8)}…</code> : '—',
      },
      { header: 'CIF', cell: (r) => <code className="cell-mono">{r.cifId ?? '—'}</code> },
      { header: 'Operator', cell: (r) => <span className="cell-desc">{r.operatorCode ?? '—'}</span> },
      {
        header: 'Mode',
        cell: (r) => <Pill tone={r.cartMode === 'Production' ? 'ok' : 'muted'}>{r.cartMode}</Pill>,
      },
      {
        header: 'Status',
        cell: (r) => (
          <Pill tone={r.success ? 'ok' : 'err'}>
            {r.success ? `HTTP ${r.httpStatus}` : r.errorCode ?? `HTTP ${r.httpStatus}`}
          </Pill>
        ),
      },
      { header: 'Latency', cell: (r) => <span className="cell-desc">{fmtLatency(r.latencyMs)}</span> },
      { header: 'Score', cell: (r) => <span className="num">{r.score ?? '—'}</span> },
      {
        header: '',
        cell: (r) => <Button onClick={() => setSelected(r)}>View</Button>,
      },
    ],
    [],
  );

  return (
    <>
      <Banner tone="info">
        <strong>PII handling:</strong> request payloads are sanitized (SSN last-4, DOB year-only)
        and then SHA-256 hashed before this table is written. The detail view shows metadata + hash,
        never raw content. Retention defaults to 84 months (7 years) per the CIP-extended rule.
      </Banner>

      <Card
        title="LexisNexis Call Log"
        actions={
          <div className="card-actions-row">
            <Button onClick={refresh}>Refresh</Button>
          </div>
        }
      >
        <div className="filter-grid">
          <label className="form-field">
            <span className="form-field__label">From (local)</span>
            <input
              type="datetime-local"
              className="form-input"
              aria-label="From"
              value={fromInput}
              onChange={(e) => setFromInput(e.target.value)}
            />
          </label>
          <label className="form-field">
            <span className="form-field__label">To (local)</span>
            <input
              type="datetime-local"
              className="form-input"
              aria-label="To"
              value={toInput}
              onChange={(e) => setToInput(e.target.value)}
            />
          </label>
          <TextField
            label="Cart ID"
            value={cartIdInput}
            onChange={setCartIdInput}
            placeholder="GUID"
            monospace
          />
          <SelectField
            label="Operation / product"
            value={productInput}
            onChange={setProductInput}
            options={PRODUCT_OPTIONS}
          />
          <NumberField label="Max rows" value={topInput} onChange={setTopInput} min={1} max={1000} />
        </div>
        <div className="card-actions-row filter-actions">
          <Button variant="primary" onClick={applyFilters}>
            Apply filters
          </Button>
          <Button onClick={clearFilters}>Clear</Button>
          {productCounts.length > 0 && (
            <span className="pill-stack">
              {productCounts.map(([p, n]) => (
                <Pill key={p} tone="muted">
                  {p}: {n}
                </Pill>
              ))}
            </span>
          )}
        </div>
      </Card>

      {loadError ? (
        <Banner tone="err">
          Could not load call log: {loadError} <Button onClick={refresh}>Retry</Button>
        </Banner>
      ) : !rows ? (
        <Spinner label="Loading call log…" />
      ) : (
        <Card title={`${rows.length} calls`}>
          <DataTable<LexisNexisCallLogRow>
            columns={columns}
            rows={rows}
            rowKey={(r) => r.callId}
            emptyState={
              <p className="empty">
                No calls recorded for the current filter. (At launch this is expected — the
                scaffold defaults to disabled.)
              </p>
            }
          />
        </Card>
      )}

      <ModalShell
        open={selected !== null}
        title={selected ? `Call ${selected.callId.slice(0, 8)}…` : ''}
        onClose={() => setSelected(null)}
        width={640}
      >
        {selected && (
          <div className="form-stack">
            <Banner tone="info">
              This audit row stores <strong>SHA-256 hashes</strong> of the sanitized request payload
              — never raw content. The sanitization pass already masked SSN (last-4 only) and DOB
              (year only) before hashing.
            </Banner>
            <dl className="detail-grid">
              <DetailRow label="Call ID" value={selected.callId} mono />
              <DetailRow label="Product / operation" value={selected.product} mono />
              <DetailRow label="Timestamp" value={fmtTs(selected.timestamp)} />
              <DetailRow label="Cart mode" value={selected.cartMode} />
              <DetailRow label="Sandbox / Prod" value={selected.sandboxOrProd ?? '—'} />
              <DetailRow label="Cart ID" value={selected.cartId ?? '—'} mono />
              <DetailRow label="CIF" value={selected.cifId ?? '—'} mono />
              <DetailRow label="Operator" value={selected.operatorCode ?? '—'} />
              <DetailRow
                label="HTTP status"
                value={`${selected.httpStatus} (${selected.success ? 'success' : 'failure'})`}
              />
              <DetailRow label="Latency" value={fmtLatency(selected.latencyMs)} />
              <DetailRow label="Score" value={selected.score?.toString() ?? '—'} />
              <DetailRow label="Conversation ID" value={selected.conversationId ?? '—'} mono />
              <DetailRow label="Error code" value={selected.errorCode ?? '—'} mono />
              <DetailRow label="Error message" value={selected.errorMessage ?? '—'} />
              <DetailRow label="Reason codes" value={selected.reasonCodesJson ?? '—'} mono />
              <DetailRow label="Request SHA-256" value={fmtShortHash(selected.requestHash)} mono />
              <DetailRow
                label="PII sub-payload SHA-256"
                value={fmtShortHash(selected.requestPiiHash)}
                mono
              />
            </dl>
          </div>
        )}
      </ModalShell>
    </>
  );
}

function DetailRow({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}): JSX.Element {
  return (
    <>
      <dt className="detail-grid__label">{label}</dt>
      <dd className={mono ? 'detail-grid__value detail-grid__value--mono' : 'detail-grid__value'}>
        {value}
      </dd>
    </>
  );
}
